import React from 'react';
import { Box, Typography } from '@mui/material';
import Lock from '@mui/icons-material/Lock';
import COLORS from '@/resources/style/Themes';

interface Props {
  locked: boolean;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}

// Frosted-glass lock overlay reused across Map, config authoring, and Respond
// management to gate content behind an entitlement without unmounting it.
//
// IMPORTANT: `children` gets a CSS `filter`, which makes this box the new
// containing block for any `position: fixed` descendant inside `children`
// (e.g. a permanent Drawer/sidebar). That breaks the descendant's
// viewport-relative sizing and can blow out the document height into a
// page-wide scroll. Callers with a fixed-position sidebar MUST render that
// sidebar as a sibling outside this component (see CompassConfigurationsPage
// and Map.jsx) and only wrap the non-fixed main content here.
const EntitlementLockOverlay: React.FC<Props> = ({
  locked,
  description,
  action,
  children,
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: 1,
          // Without an explicit height, this plain block child collapses to its
          // content's auto height even when the root above has a definite height
          // from flex — breaking height:100% for any child (e.g. a full-bleed map).
          minHeight: 0,
          filter: locked ? 'blur(4px)' : 'none',
          pointerEvents: locked ? 'none' : 'auto',
          userSelect: locked ? 'none' : 'auto',
        }}
        aria-hidden={locked}
      >
        {children}
      </Box>
      {locked && (
        <Box
          role="alert"
          sx={{
            position: 'absolute',
            inset: -16,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            backgroundColor: 'rgba(255, 255, 255, 0.13)',
            backdropFilter: 'blur(2px)',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Lock sx={{ fontSize: 82, color: COLORS.SAFEGUARD_BLUE }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, fontSize: 16, color: COLORS.SAFEGUARD_BLUE }}
          >
            {description}
          </Typography>
          {action}
        </Box>
      )}
    </Box>
  );
};

export default EntitlementLockOverlay;
